// utils/fieldValueValidation.ts
//
// Runtime value validation — checks the *values a user typed* against a
// field's required/format rules (used by DynamicForm at fill-time).
// Distinct from utils/validation.ts, which validates a field's *schema*
// definition inside the builder editor (unique keys, orderNo, etc.).

import { BuilderField } from '../types/BuilderField';

const PHONE_REGEX = /^\+?[0-9]{10,15}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isEmptyValue(value: any): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Validates a single field's value against its own rules (required + type-specific
 * format). Returns an error message string, or null if valid.
 */
export function validateFieldValue(field: BuilderField, value: any): string | null {
  const empty = isEmptyValue(value);

  if (field.required && empty) {
    return `${field.label} is required`;
  }

  // Optional fields with no value are valid — nothing further to check.
  if (empty) return null;

  switch (field.fieldType) {
    case 'email': {
      if (!EMAIL_REGEX.test(String(value).trim())) {
        return 'Enter a valid email address';
      }
      return null;
    }
    case 'phone': {
      const digitsOnly = String(value).replace(/[\s\-()]/g, '');
      if (!PHONE_REGEX.test(digitsOnly)) {
        return 'Enter a valid phone number (10–15 digits)';
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * Validates every active field against `values`. Returns a map of
 * fieldKey -> error message, containing only the fields that failed.
 */
export function validateFieldValues(
  fields: BuilderField[],
  values: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};
  fields
    .filter((f) => f.active)
    .forEach((f) => {
      const err = validateFieldValue(f, values[f.fieldKey]);
      if (err) errors[f.fieldKey] = err;
    });
  return errors;
}